import {
  Avatar,
  Badge,
  Card,
  Checkbox,
  HStack,
  IconButton,
  Td,
  Text,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import React, { FC, useEffect, useMemo, useState } from 'react';
import useFetch, { IncomingOptions } from 'use-http';
import { User } from '../../store/slices/User/reducer';
import { FiDownload, FiEdit3, FiFileText, FiTrash2, FiUserPlus } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { UserRole, UserStatus, filterUsersBySearch, formatExportUserData, getUserStatusLabel, getUserTypeLabel, sortUsers } from '../../util/user';
import { api, makeHeaders } from '../../util/api';
import LTable, { ToolbarItem } from '../../components/LTable';
import { formatDate, phoneFormat } from '../../util/string';
import { usePagination } from '../../hooks/usePagination';
import { isSafe } from '../../util/array';
import { ERROR_MESSAGE, useNotify } from '../../hooks/useNotify';
import { useDialog } from '../../hooks/useDialog';
import { exportToExcel } from '../../util/fileExporter';

const Users: FC = () => {
  const notify = useNotify()
  const {
    createDialog,
    closeDialog,
    setLoading: setDialogLoading,
  } = useDialog();

  const navigate = useNavigate();
  const [rawUsers, setRawUsers] = useState<User[]>([]);
  const [search, setSearch] = useState<string>('');
  const [selection, setSelection] = useState<number[]>([]);
  const [sort, setSort] = useState<{ by: string; direction: 'asc' | 'desc' }>({
    by: '',
    direction: 'asc',
  });

  const canArchive = selection.length > 0 && (
    selection.some((id) => rawUsers.find((user) => user.id === id)?.status === UserStatus.ACTIVE)
  );

  const handleAddUser = () => {
    navigate('/colaboradores/nuevo');
  };

  const handleArchiveUsers = () => {
    createDialog({
      title: 'Archivar colaboradores',
      content: '¿Estás seguro que deseas archivar a estos usuarios?',
      onConfirm: handleDeleteMultipleUsers,
      onCancel: closeDialog,
      confirmText: 'Sí, archivar',
    });
  };

  const handleExportToExcel = () => {
    createDialog({
      title: 'Exportar',
      content: '¿Desea exportar la lista de usuarios a Excel?',
      onConfirm: () => exportToExcel('colaboradores', formatExportUserData(rawUsers)),
      onCancel: closeDialog,
      confirmText: 'Exportar',
    });
  };

  const rowSelectionHandler = (e: any) => {
    const userId = Number(e.target.value);
    if (e.target.checked) {
      setSelection([...selection, userId]);
    } else {
      setSelection(selection.filter((id) => id !== userId));
    }
  };

  const columns = [{
    label: '',
  }, {
    label: 'Colaborador',
    value: 'firstName',
  }, {
    label: 'Función',
    value: 'role',
  }, {
    label: 'Fecha de alta',
    value: 'createdAt',
  }, {
    label: 'Estado',
    value: 'status',
  }, {
    label: 'Acciones',
  }];

  const toolbar: ToolbarItem[] = [{
    type: 'button',
    label: 'Nuevo',
    icon: <FiUserPlus />,
    onClick: handleAddUser,
  },
  {
    type: 'button',
    label: 'Archivar',
    icon: <FiTrash2 />,
    disabled: !canArchive,
    onClick: handleArchiveUsers,
  },
  {
    type: 'button',
    label: 'Exportar a Excel',
    icon: <FiDownload />,
    onClick: handleExportToExcel,
  }];

  const searchHandler = (query: string) => {
    setSearch(query);
  };

  const sortHandler = (head: string, direction: 'asc' | 'desc') => {
    setSort({ by: head, direction });
  };

  const filteredUsers = useMemo(() => {
    const filterd = filterUsersBySearch(rawUsers, search);
    return sortUsers(filterd, sort.by, sort.direction);
  }, [rawUsers, search, sort]);

  const rowsPerPageOptions = [10, 25, 50, 100];
  const {
    data: users,
    page,
    rowsPerPage,
    setRowsPerPage,
    nextPage,
    prevPage,
    canPrevPage,
    canNextPage,
  } = usePagination(filteredUsers, rowsPerPageOptions[0]);

  const {
    get: fetchUsers,
    patch: deactivateUser,
    cache,
    loading,
    response,
    error,
  } = useFetch(`${api}/users`, makeHeaders() as IncomingOptions);

  const refreshUsers = async () => {
    cache.clear();
    const updatedUsers = await fetchUsers();
    if (response.ok) {
      setRawUsers(isSafe(updatedUsers, error))
    } else {
      notify(ERROR_MESSAGE, 'error');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!Number.isInteger(userId)) {
      closeDialog();
      return;
    }

    try {
      cache.clear();
      setDialogLoading(true);
      await deactivateUser(`/${userId}`, {
        status: UserStatus.DISABLED
      });

      if (response.ok) {
        notify('Colaborador archivado', 'success');
        refreshUsers();
      } else {
        notify(ERROR_MESSAGE, 'error');
      }
    } catch (e) {
      notify(ERROR_MESSAGE, 'error');
    } finally {
      setDialogLoading(false);
      closeDialog();
    }
  };

  const handleDeleteMultipleUsers = async () => {
    if (!selection.length) {
      return;
    }

    try {
      cache.clear();
      setDialogLoading(true);
      await deactivateUser(`/status/toggle`, {
        ids: selection,
      });

      if (response.ok) {
        notify('Colaboradores archivados', 'success');
        refreshUsers();
        setSelection([]);
      } else {
        throw new Error();
      }
    } catch (e) {
      notify(ERROR_MESSAGE, 'error');
    }
    finally {
      setDialogLoading(false);
      closeDialog();
    }
  };

  const archiveUser = async (id: number) => {
    await createDialog({
      title: 'Archivar colaborador',
      content: '¿Estás seguro que deseas archivar a este usuario?',
      onConfirm: () => handleDeleteUser(id),
      onCancel: closeDialog,
      confirmText: 'Sí, archivar',
    });
  };

  useEffect(() => {
    refreshUsers(); // eslint-disable-next-line
  }, []);

  return (
    <>
      <Card gap={8}>
        <LTable
          loading={loading}
          searchable
          searchPlaceholder='Buscar colaborador...'
          columns={columns}
          onSearch={searchHandler}
          sortable
          onSort={sortHandler}
          paginable
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={filteredUsers.length}
          pageOptions={rowsPerPageOptions}
          onPrevPage={prevPage}
          onNextPage={nextPage}
          canPrevPage={canPrevPage}
          canNextPage={canNextPage}
          onRowsPerPageChange={setRowsPerPage}
          toolbar={toolbar}
        >
          {!users.length && (
            <Tr>
              <Td fontSize='sm' fontWeight='medium' color='gray.500' colSpan={columns.length} textAlign='center'>
                {loading && 'Cargando...'}
                {!loading && 'No hay datos para mostrar.'}
              </Td>
            </Tr>
          )}
          {users.map((user: User, index: number) => (
            <Tr key={index}>
              <Td w='1em'>
                <Checkbox
                  colorScheme='linkedin'
                  value={user.id}
                  checked={selection.includes(user.id)}
                  onChange={rowSelectionHandler}
                />
              </Td>
              <Td>
                <HStack gap={4}>
                  <Avatar
                    size='sm'
                    name={user.firstName + ' ' + user.lastName}
                  />

                  <VStack align='start' gap={0}>
                    <Text fontSize='sm' fontWeight='semibold'>{`${user.firstName} ${user.lastName}`}</Text>
                    <Text fontSize='xs' color='gray.500'>{user.email}</Text>
                  </VStack>
                </HStack>
              </Td>
              <Td>
                <VStack align='start' gap={0}>
                  <Text fontSize='sm' fontWeight='semibold'>{getUserTypeLabel(user.role as UserRole)}</Text>
                  <Text fontSize='xs' color='gray.500'>
                    {phoneFormat(user.phone, 'Sin número')}
                  </Text>
                </VStack>
              </Td>
              <Td>
                <Text fontSize='sm'>
                  {formatDate(user.createdAt)}
                </Text>
              </Td>
              <Td>
                <Badge
                  variant='solid'
                  minW={84}
                  textAlign='center'
                  colorScheme={user.status === UserStatus.ACTIVE ? 'green' : 'gray'}
                >
                  <Text>{getUserStatusLabel(user.status as UserStatus)}</Text>
                </Badge>
              </Td>
              <Td>
                <HStack>
                  <Tooltip label={'Editar'} placement={'top'} rounded={'md'}>
                    <IconButton
                      as={Link}
                      isRound
                      aria-label='Editar'
                      icon={<FiEdit3 />}
                      to={`${user.id}/editar`}
                    />
                  </Tooltip>
                  <Tooltip label={'Archivar'} placement={'top'} rounded={'md'}>
                    <IconButton
                      isRound
                      isDisabled={user.status === UserStatus.DISABLED}
                      aria-label='Archivar'
                      icon={<FiTrash2 />}
                      onClick={(e: any) => archiveUser(user.id)}
                    />
                  </Tooltip>
                  <Tooltip label='Ver actividad' placement={'top'} rounded={'md'}>
                    <IconButton
                      as={Link}
                      isRound
                      aria-label='Editar'
                      icon={<FiFileText />}
                      to={`${user.id}`}
                    />
                  </Tooltip>
                </HStack>
              </Td>
            </Tr>
          ))}
        </LTable>
      </Card>
    </>
  );
};

export default Users;
